import { ReactNode } from 'react';

interface DataTableProps {
  children: ReactNode;
}

export function DataTable({ children }: DataTableProps) {
  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          {children}
        </table>
      </div>
    </div>
  );
}

interface DataTableHeadProps {
  children: ReactNode;
}

export function DataTableHead({ children }: DataTableHeadProps) {
  return (
    <thead className="bg-gray-50 dark:bg-gray-800">
      <tr>{children}</tr>
    </thead>
  );
}

interface DataTableBodyProps {
  children: ReactNode;
}

export function DataTableBody({ children }: DataTableBodyProps) {
  return (
    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
      {children}
    </tbody>
  );
}

interface DataTableFootProps {
  children: ReactNode;
}

/**
 * Table footer for summary rows (task 1010). Separated from the body by its own
 * top border so a total never reads as one more data row.
 */
export function DataTableFoot({ children }: DataTableFootProps) {
  return (
    <tfoot className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
      {children}
    </tfoot>
  );
}

interface DataTableRowProps {
  children: ReactNode;
  onClick?: () => void;
}

export function DataTableRow({ children, onClick }: DataTableRowProps) {
  return (
    <tr
      className={`${onClick ? 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

interface DataTableCellProps {
  children: ReactNode;
  className?: string;
  /** Span multiple columns — used by footer summary rows (task 1010). */
  colSpan?: number;
}

export function DataTableCell({ children, className = '', colSpan }: DataTableCellProps) {
  return (
    <td className={`px-6 py-4 ${className}`} colSpan={colSpan}>
      {children}
    </td>
  );
}

interface TableHeaderProps {
  className?: string;
  children?: ReactNode;
}

export function TableHeader({ className = '', children }: TableHeaderProps) {
  return (
    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}
